// ─── BRAND NAME — THE ONLY PLACE TO CHANGE THE COMPANY / PRODUCT NAME ────────
//
//   Edit the values below and every piece of display text in the app follows:
//   sidebar title, login screen, browser tab title, email subjects and
//   sign-offs, "Redlane Credit" labels, the "Redlane" badge, and so on.
//
//   Colors live in theme.js. This file holds the name, backend URL and fonts.
//
//   What this deliberately does NOT rename (changing these would break data):
//     • database values like "Redlane_credit" and fields like RedlaneCreditApplied
//     • the login storage key and the shared password (in auth.jsx)
// ─────────────────────────────────────────────────────────────────────────────

const NAME         = "Redlanetravels";      // ← full company name
const SHORT_NAME   = "Redlane";          // ← short name, used for the credit product and badges
const EMAIL_DOMAIN = "Redlanetravels.com";  // ← staff email domain allowed to log in
const PRODUCT      = "CRM";          // ← suffix shown after the name in titles
const TAGLINE      = "Agency Suite"; // ← small line under the sidebar title
const DEFAULT_CURRENCY = "NGN";      // ← pre-selected currency on invoices, receipts, refunds,
                                     //   finances, trip payments and currency switchers

// Backend server the app talks to. Swap the comment to work against a local server.
const API_URL      = "https://redlane-crm.onrender.com";
// const API_URL   = "http://localhost:8081";

// Optional: rename the credit product independently, e.g. "Travel Credit".
// Leave "" to use "<SHORT_NAME> Credit".
const CREDIT_NAME_OVERRIDE = "";

// ─── Derived values (no need to edit) ────────────────────────────────────────
export const BRAND = {
  name: NAME,                                   // "Redlanetravels"
  nameUpper: NAME.toUpperCase(),                // "Redlanetravels"
  shortName: SHORT_NAME,                        // "Redlane"
  shortUpper: SHORT_NAME.toUpperCase(),         // "Redlane"
  creditName: CREDIT_NAME_OVERRIDE || `${SHORT_NAME} Credit`, // "Redlane Credit"
  appName: `${NAME.toUpperCase()} ${PRODUCT}`,  // "Redlanetravels CRM"
  tagline: TAGLINE,
  emailDomain: EMAIL_DOMAIN,
  currency: DEFAULT_CURRENCY,                   // default selected currency everywhere                    // "Redlanetravels.com"
  // A REACT_APP_API_URL environment variable (e.g. in .env.local) overrides API_URL,
  // so each machine or deployment can point elsewhere without editing this file.
  apiUrl: (process.env.REACT_APP_API_URL || API_URL).replace(/\/+$/, ""), // no trailing slash
};

// ─── FONTS ───────────────────────────────────────────────────────────────────
// Heading font = page titles (h1–h3) and the brand name. Body font = everything else.
const HEADING_FONT = "Quincy CF";      // ← heading / brand font (no file yet → shows Fraunces)
const BODY_FONT    = "Circe Rounded";  // ← body font (licensed file in public/fonts)
//
// Free alternatives, already set up (just change the names above):
//   headings: "Fraunces", "Young Serif", "Newsreader"
//   body:     "Jost", "Outfit", "Urbanist", "DM Sans"
// Any other Google Font works too: add it to GOOGLE_FONTS below.
//
// Licensed fonts (e.g. "Quincy CF", "Circe"): set the names above, then load them
// through FONT_FILES or FONT_STYLESHEETS further down.

// Used if the fonts above ever fail to load.
const HEADING_FALLBACK = `"Fraunces", Georgia, serif`;
const BODY_FALLBACK    = `"DM Sans", system-ui, -apple-system, "Segoe UI", sans-serif`;

// Google Fonts the app knows how to load, with the weights to fetch.
// Fonts listed here are loaded automatically when used above — no link needed.
const GOOGLE_FONTS = {
  "Fraunces":    "ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700",
  "Young Serif": "",                                        // has one weight only
  "Newsreader":  "opsz,wght@6..72,400;6..72,600;6..72,700",
  "Jost":        "wght@300;400;500;600;700",
  "Outfit":      "wght@300;400;500;600;700",
  "Urbanist":    "wght@300;400;500;600;700",
  "DM Sans":     "wght@300;400;500;600;700",
};

// Extra stylesheets, e.g. an Adobe Fonts kit link for licensed fonts.
const FONT_STYLESHEETS = [];

// Your own licensed font files. Put them in  public/fonts/  and list them here.
// "family" must match HEADING_FONT / BODY_FONT exactly.
const FONT_FILES = [
  { family: "Circe Rounded", weight: 400, style: "normal", src: "/fonts/CirceRounded-Regular_400.otf" },
  // Add more weights as you get them, e.g.:
  // { family: "Circe Rounded", weight: 700, style: "normal", src: "/fonts/CirceRounded-Bold.otf" },
  // { family: "Quincy CF",     weight: 700, style: "normal", src: "/fonts/QuincyCF-Bold.woff2" },
];

// ─── Derived font CSS (no need to edit) ──────────────────────────────────────
const fmt = (src) => {
  const ext = String(src).split("?")[0].split(".").pop().toLowerCase();
  return { woff2: "woff2", woff: "woff", ttf: "truetype", otf: "opentype" }[ext] || ext;
};

// Load the chosen fonts from Google if listed there. Fallback fonts are only
// downloaded when a chosen font isn't a Google font (e.g. licensed files).
const firstFamily = (stack) => stack.split(",")[0].replace(/["']/g, "").trim();
const googleNeeded = [...new Set([
  HEADING_FONT in GOOGLE_FONTS ? HEADING_FONT : firstFamily(HEADING_FALLBACK),
  BODY_FONT in GOOGLE_FONTS ? BODY_FONT : firstFamily(BODY_FALLBACK),
])].filter((name) => name in GOOGLE_FONTS);

const googleHref = googleNeeded.length
  ? "https://fonts.googleapis.com/css2?" +
    googleNeeded
      .map((name) => {
        const axes = GOOGLE_FONTS[name];
        return "family=" + name.replace(/ /g, "+") + (axes ? ":" + axes : "");
      })
      .join("&") +
    "&display=swap"
  : null;

export const FONTS = {
  heading: `"${HEADING_FONT}", ${HEADING_FALLBACK}`,
  body: `"${BODY_FONT}", ${BODY_FALLBACK}`,
};

// Injected once by App.js. @import rules come first, as CSS requires.
export const FONT_CSS = [
  ...[googleHref, ...FONT_STYLESHEETS].filter(Boolean).map((href) => `@import url('${href}');`),
  ...FONT_FILES.map((f) => `@font-face {
    font-family: "${f.family}";
    src: url("${f.src}") format("${fmt(f.src)}");
    font-weight: ${f.weight || 400};
    font-style: ${f.style || "normal"};
    font-display: swap;
  }`),
  `:root { --font-heading: ${FONTS.heading}; --font-body: ${FONTS.body}; }`,
].join("\n");
